import {
    ComponentRef,
    Directive,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    Renderer2,
    SimpleChanges,
    ViewContainerRef,
} from '@angular/core';
import { MatProgressSpinner, ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { GrowlerService } from '../../../shared/growler/growler.service';

/**
 * Directive that adds horizontal spacing between buttons in toolbars/button groups.
 *
 * @styles /ui/src/styles/ui/mixins/_v2_buttons.scss
 * - .button-spacer
 * - .button-spacer.xs
 * - .button-spacer.sm
 * - .button-spacer.lg
 */
@Directive({
    selector: '[button-spacer], button-spacer, [buttonSpacer]',
    standalone: true,
    host: { class: 'button-spacer' },
})
export class ButtonSpacerDirective
{
    @Input() size: 'xs' | 'sm' | 'md' | 'lg' = 'md';

    @HostBinding('class.xs') get xSmallSpacer() {
        return this.size === 'xs';
    }

    @HostBinding('class.sm') get smallSpacer() {
        return this.size === 'sm';
    }

    @HostBinding('class.lg') get largeSpacer() {
        return this.size === 'lg';
    }
}

/**
 * Directive that styles buttons as text links with v2-layout link styling.
 * Automatically detects if button is Material button or inline element.
 *
 * @styles /ui/src/styles/ui/mixins/_v2_buttons.scss (also referenced in _v2_dialogs.scss, _v2_lists.scss)
 * - .link-button
 * - .inline-link-button
 */
@Directive({
    selector: '[linkButton]',
    standalone: true,
    host: { class: 'link-button' },
})
export class LinkButtonDirective
{
    constructor(private _el: ElementRef) {
    }

    @HostBinding('class.inline-link-button') get inlineLinkClass() {
        return !this._el.nativeElement.classList.contains('mdc-button')
            && !this._el.nativeElement.classList.contains('mdc-icon-button')
            && !this._el.nativeElement.classList.contains('mdc-fab');
    }
}

/**
 * Directive that makes a mat-icon behave as a clickable action icon.
 * Adds link color, hover/focus states, keyboard accessibility (tabindex, role, enter/space).
 *
 * @styles /ui/src/styles/ui/mixins/_v2_buttons.scss
 * - .clickable-icon
 *
 * @example
 * <mat-icon clickableIcon [svgIcon]="'icon-v2:edit'" (click)="onEdit()"></mat-icon>
 * <mat-icon clickableIcon matIconSuffix [svgIcon]="'icon-v2:edit'" (click)="onEdit()"></mat-icon>
 */
@Directive({
    selector: 'mat-icon[clickableIcon]',
    standalone: true,
    host: { class: 'clickable-icon' },
})
export class ClickableIconDirective
{
    @HostBinding('attr.tabindex') readonly tabindex = 0;
    @HostBinding('attr.role') readonly role = 'button';

    @HostListener('keydown.enter', ['$event'])
    @HostListener('keydown.space', ['$event'])
    onKeydown(event: KeyboardEvent): void {
        event.preventDefault();
        (event.target as HTMLElement).click();
    }
}

/**
 * Directive that styles buttons with nested checkbox with v2-layout checkbox styling.
 * Used for checkbox-like buttons in toolbars.
 *
 * @styles /ui/src/styles/ui/mixins/_v2_buttons.scss
 * - .checkbox-button
 */
@Directive({
    selector: 'button[checkboxButton]',
    standalone: true,
    host: { class: 'checkbox-button' },
})
export class CheckboxButtonDirective
{
}

/**
 * Directive for "Add Another" action buttons with link styling.
 * Combines add-another-button and link-button classes.
 *
 * @styles /ui/src/styles/ui/mixins/_v2_buttons.scss
 * - .add-another-button (also inherits .link-button styles)
 */
@Directive({
    selector: 'button[addAnotherButton], a[addAnotherButton]',
    standalone: true,
    host: { class: 'add-another-button link-button' },
})
export class AddAnotherButtonDirective
{
}

/**
 * Directive that positions icon at the end of Material button text.
 *
 * @styles /ui/src/styles/ui/mixins/_v2_buttons.scss
 * - .button-icon-suffix
 */
@Directive({
    selector: 'button[matButtonIconSuffix], a[matButtonIconSuffix]',
    standalone: true,
    host: { class: 'button-icon-suffix' },
})
export class MatButtonIconSuffixDirective
{
}

/**
 * Directive that styles buttons to fill the width with their container.
 *
 * @styles /ui/src/styles/ui/mixins/_v2_buttons.scss
 * - .full-width-button
 */
@Directive({
    selector: 'button[fullWidthButton], a[fullWidthButton]',
    standalone: true,
    host: { class: 'full-width-button' },
})
export class FullWidthButtonDirective {
}

/**
 * Directive that styles button groups to be used as a row of buttons.
 * Applies horizontal spacing between buttons.
 *
 * @styles /ui/src/styles/ui/mixins/_v2_buttons.scss
 * - .button-group
 */
@Directive({
    selector: '[button-group], button-group, [buttonGroup]',
    standalone: true,
    host: { class: 'button-group' },
})
export class ButtonGroupDirective
{
}

/**
 * Directive that dynamically injects a MatProgressSpinner into a button or anchor element.
 * The spinner is prepended as the first child when showSpinner is true.
 *
 * @example
 * <button mat-flat-button [showSpinner]="isLoading">Save</button>
 * <button mat-flat-button [showSpinner]="isLoading" [spinnerSize]="16">Save</button>
 * <button mat-flat-button [showSpinner]="isLoading" spinnerMode="determinate">Save</button>
 *
 * @styles /ui/src/styles/ui/mixins/_v2_buttons.scss
 * - .spinner-active
 */
@Directive({
    selector: 'button[showSpinner], a[showSpinner]',
    standalone: true,
})
export class ButtonShowSpinnerDirective implements OnChanges
{
    @Input() showSpinner = false;
    @Input() spinnerSize = 20;
    @Input() spinnerMode: ProgressSpinnerMode = 'indeterminate';

    private _spinnerRef: ComponentRef<MatProgressSpinner> | null = null;

    constructor(
        private _el: ElementRef,
        private _renderer: Renderer2,
        private _viewContainerRef: ViewContainerRef,
    ) {}

    @HostBinding('class.spinner-active') get spinnerActiveClass() {
        return this.showSpinner;
    }

    ngOnChanges({ showSpinner }: SimpleChanges): void {
        if (showSpinner) {
            if (showSpinner?.currentValue) {
                this._createSpinner();
            } else {
                this._destroySpinner();
            }
        }
    }

    private _createSpinner(): void {
        if (this._spinnerRef) {
            return;
        }

        this._spinnerRef = this._viewContainerRef.createComponent(MatProgressSpinner);
        this._spinnerRef.instance.diameter = this.spinnerSize;
        this._spinnerRef.instance.mode = this.spinnerMode;

        const spinnerElement = this._spinnerRef.location.nativeElement;
        const hostElement = this._el.nativeElement;
        this._renderer.insertBefore(hostElement, spinnerElement, hostElement.firstChild);
    }

    private _destroySpinner(): void {
        if (this._spinnerRef) {
            this._spinnerRef.destroy();
            this._spinnerRef = null;
        }
    }
}

/**
 * Directive that styles mat-icon-button as a square-ish button with rounded corners.
 * Useful for toolbar/footer action buttons.
 *
 * Variants (add as additional class or input):
 * - default: White background, blue hover
 * - primary: Blue background (for send/submit buttons)
 * - danger: Red hover state (for delete/remove buttons)
 *
 * @styles /ui/src/styles/ui/mixins/_v2_buttons.scss
 * - squareIconButton mixin
 * - .square-icon-btn-primary
 * - .square-icon-btn-danger
 *
 * @example
 * <button mat-icon-button squareIconButton>...</button>
 * <button mat-icon-button squareIconButton="primary">...</button>
 * <button mat-icon-button squareIconButton="danger">...</button>
 */
@Directive({
    selector: 'button[squareIconButton]',
    standalone: true,
    host: { class: 'square-icon-btn' },
})
export class SquareIconButtonDirective
{
    @Input() squareIconButton: 'default' | 'primary' | 'danger' | '' = 'default';

    @HostBinding('class.square-icon-btn-primary') get primaryClass() {
        return this.squareIconButton === 'primary';
    }

    @HostBinding('class.square-icon-btn-danger') get dangerClass() {
        return this.squareIconButton === 'danger';
    }
}

/** What a copy button reports back after the write. `null` ends the confirmation. */
export type CopyConfirmState = 'copied' | 'failed' | null;

/**
 * Directive that copies a value to the clipboard when clicked.
 *
 * Two confirmation styles:
 *  - `growler` (default) — a toast, for one-off "Copy link" buttons.
 *  - `inline` — no toast; the directive emits `copyStateChange` so the host can
 *    confirm ON the button ("Copied") and reset itself. Use this where the page
 *    holds MANY copy buttons — a per-row or per-message toast is noise, and the
 *    confirmation belongs next to the thing that was copied.
 *
 * @example
 * <button mat-flat-button [copyToClipboard]="urlToCopy">Copy link</button>
 * <button mat-flat-button [copyToClipboard]="urlToCopy" copySuccessTitle="Copied!" copySuccessMessage="Link copied">Copy</button>
 * <button [copyToClipboard]="text" copyConfirm="inline" (copyStateChange)="state = $event">Copy</button>
 */
@Directive({
    selector: '[copyToClipboard]',
    standalone: true,
})
export class CopyToClipboardDirective implements OnDestroy
{
    /** The value to copy to clipboard */
    @Input() copyToClipboard: string;

    /** Title for the success growler (default: 'Link copied') */
    @Input() copySuccessTitle = 'Link copied';

    /** Message for the success growler (default: 'Link copied to clipboard') */
    @Input() copySuccessMessage = 'Link copied to clipboard';

    /** `growler` toasts the result; `inline` reports it through `copyStateChange`. */
    @Input() copyConfirm: 'growler' | 'inline' = 'growler';

    /** How long an inline confirmation stands before it resets to `null` (ms). */
    @Input() copyConfirmMs = 2000;

    /**
     * Inline mode only: `'copied'`/`'failed'` immediately, then `null` once the
     * confirmation window closes, so the host never has to run its own timer.
     */
    @Output() copyStateChange = new EventEmitter<CopyConfirmState>();

    private _resetTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(private _growler: GrowlerService) {}

    ngOnDestroy(): void {
        this._clearTimer();
    }

    @HostListener('click', ['$event'])
    onClick(event: Event): void {
        // Prevent event bubbling to avoid triggering parent click handlers or form submission
        event.stopPropagation();

        // Never write an empty string: it silently destroys whatever the user
        // had on the clipboard, and reporting success while doing it is worse.
        // Growler mode keeps its original silent no-op here — a toast on a
        // value that is merely still loading would be new noise for callers
        // that never asked for it.
        if (!this.copyToClipboard) {
            if (this.copyConfirm === 'inline') {
                this._settle('failed');
            }
            return;
        }
        if (!navigator.clipboard?.writeText) {
            this._settle('failed');
            return;
        }

        navigator.clipboard.writeText(this.copyToClipboard).then(
            () => this._settle('copied'),
            () => this._settle('failed'),
        );
    }

    private _settle(state: 'copied' | 'failed'): void {
        if (this.copyConfirm !== 'inline') {
            if (state === 'copied') {
                this._growler.success(this.copySuccessTitle, this.copySuccessMessage);
            } else {
                this._growler.oops('Failed to copy to clipboard');
            }
            return;
        }

        this._clearTimer();
        this.copyStateChange.emit(state);
        this._resetTimer = setTimeout(() => {
            this._resetTimer = null;
            this.copyStateChange.emit(null);
        }, this.copyConfirmMs);
    }

    private _clearTimer(): void {
        if (this._resetTimer !== null) {
            clearTimeout(this._resetTimer);
            this._resetTimer = null;
        }
    }
}
